// @ts-nocheck
/*
 * Campaign report: campaign header + Details / Schedule tiles (view=header), then the
 * shared per-call report (call-report/CallReportView, view=calls) used by the CDR report
 * and DID view details: one row per call, auto-loaded keyset batches, 7-day range,
 * browser-side call filter, charts toggle and full export. Campaign-only columns
 * (Customer Details, Retry, IVR Name, Agents Dialed, Pulses, Trunk) come from the backend.
 */
import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Button as BsButton } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { FiArrowLeft, FiPhone, FiSlash, FiHash, FiClock, FiUsers, FiRefreshCw } from 'react-icons/fi';
import Loader from '@/components/partials/components/Loader';
import { getApiCall } from '@/service/httpCall';
import { API_CAMPAIGN_REPORT, CDR_TRACE_REPORT } from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import CallReportView from '@/features/ivr-reports/call-report/CallReportView';

const DIALER_TYPE_LABEL = {
  1: 'Click2Call',
  2: 'Auto Dialer',
  3: 'Predictive Dialer',
  4: 'Power Dialer',
};

const TEMPLATE_LABEL = { 0: 'Simple IVR', 1: 'DTMF', 2: 'Call Patch', 3: 'Custom IVR' };

const CAMPAIGN_STATUS_LABEL = {
  0: 'Ready',
  1: 'Running',
  2: 'Completed',
  3: 'Low Balance',
  4: 'Error',
  5: 'Error',
  6: 'Cancelling',
  7: 'Cancelled',
  8: 'Pausing',
  9: 'Paused',
  10: 'Resuming',
  21: 'DND Processing',
  22: 'Waiting for Channel',
};

const RANGE_DAYS = 7;

const formatDateOnly = (value) => {
  if (!value) return '—';
  const asDate = new Date(`${value}T00:00:00`);
  if (Number.isNaN(asDate.getTime())) return String(value);
  return asDate.toLocaleDateString('en-IN');
};

const formatTimeOnly = (value) => {
  if (!value) return '—';
  return String(value).slice(0, 5);
};

const campaignDay = (value) => {
  const m = moment(String(value || '').slice(0, 10), 'YYYY-MM-DD', true);
  return m.isValid() ? m : null;
};

const campaignRange = (campaign) => {
  const first = campaignDay(campaign?.start_date);
  const last = campaignDay(campaign?.end_date);
  const end = last && last.isBefore(moment(), 'day') ? last.clone().endOf('day') : moment();
  let start = end.clone().subtract(RANGE_DAYS - 1, 'days').startOf('day');
  if (first && start.isBefore(first)) start = first.clone().startOf('day');
  if (start.isAfter(end)) start = end.clone().startOf('day');
  return { start: start.toDate(), end: end.toDate() };
};

const StatTile = ({ icon, label, value, hint }: any) => (
  <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-card p-3 shadow-neu-raised">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
      {icon}
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="truncate text-lg font-bold tabular-nums text-foreground">
        {value}
      </div>
      {hint ? (
        <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>
      ) : null}
    </div>
  </div>
);

const CampaignReport = () => {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();

  const [, setShowToast] = useState(false);
  const [, setToastProps] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [tableRun, setTableRun] = useState(0);

  const fetchHeader = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const resp = await getApiCall(`${API_CAMPAIGN_REPORT.replace(':id', campaignId)}?view=header`, {});
      setCampaign(resp?.campaign || null);
      setSummary(resp?.summary || null);
    } catch (err) {
      if (typeof err !== 'object' && String(err).includes('TokenExpiredError')) {
        errorCheck(err, navigate, setToastProps, setShowToast);
      } else {
        toast.error(friendlyApiError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [campaignId, navigate]);

  useEffect(() => {
    fetchHeader();
  }, [fetchHeader]);

  if (loading && !campaign) return <Loader />;

  const statusLabel = CAMPAIGN_STATUS_LABEL[Number(campaign?.campaign_status)] ?? '—';
  const reportBase = API_CAMPAIGN_REPORT.replace(':id', campaignId);

  return (
    <FillPage>
      <PageHeader
        title={campaign?.campaign_name || `Campaign #${campaignId}`}
        description={`ID: ${campaignId} · ${
          DIALER_TYPE_LABEL[Number(campaign?.campaign_type)] || '—'
        } · ${TEMPLATE_LABEL[Number(campaign?.template_id)] ?? '—'}${
          campaign?.first_call ? ` · First Call: ${campaign.first_call}` : ''
        } · Status: ${statusLabel}`}
        actions={
          <>
            <BsButton
              variant="outline"
              size="sm"
              onClick={() => navigate(`/${ROUTES.CAMPAIGNS.LIST}`)}
              className="gap-1.5"
            >
              <FiArrowLeft />
              Back
            </BsButton>
            <BsButton
              size="sm"
              disabled={loading}
              onClick={() => {
                fetchHeader();
                setTableRun((n) => n + 1);
              }}
              className="gap-1.5"
              title="Refresh campaign details and calls"
            >
              <FiRefreshCw className={loading ? 'animate-spin' : ''} />
              Refresh
            </BsButton>
          </>
        }
      />
      <div className="campaign-report-page mt-4 min-h-0 flex-1 overflow-y-auto">
        <Card className="mb-3">
          <CardContent>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Campaign Details</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              <StatTile icon={<FiHash />} label="Total Contacts" value={Number(campaign?.total_nos || 0)} />
              <StatTile icon={<FiPhone />} label="Processed" value={Number(campaign?.nos_processed || 0)} />
              <StatTile icon={<FiPhone />} label="Total Calls Initiated" value={Number(campaign?.total_calls_count || 0)} />
              <StatTile icon={<FiSlash />} label="DnD Skipped" value={Number(campaign?.dnd_count || 0)} />
              <StatTile
                icon={<FiClock />}
                label="Auto Retries"
                value={Number(campaign?.auto_retry_count || 0)}
                hint={`Max ${Number(campaign?.max_retries || 0)}`}
              />
              <StatTile icon={<FiUsers />} label="Unique Numbers Dialed" value={Number(summary?.unique_numbers || 0)} />
            </div>

            <div className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Schedule</div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              <StatTile icon={<FiClock />} label="Start Date" value={formatDateOnly(campaign?.start_date)} />
              <StatTile icon={<FiClock />} label="End Date" value={formatDateOnly(campaign?.end_date)} />
              <StatTile
                icon={<FiClock />}
                label="Calling Hours"
                value={`${formatTimeOnly(campaign?.start_hours)} – ${formatTimeOnly(campaign?.end_hours)}`}
              />
              <StatTile
                icon={<FiRefreshCw />}
                label="Auto Retry"
                value={Number(campaign?.is_retry) ? 'On' : 'Off'}
                hint={Number(campaign?.is_retry) ? `Every ${Number(campaign?.retry_interval || 0)} min` : undefined}
              />
            </div>
          </CardContent>
        </Card>

        {campaign ? (
          <CallReportView
            key={`${campaignId}:${tableRun}`}
            campaignMode
            persistKey={`campaigns/CampaignReport/${campaignId}`}
            buildUrl={(query) => `${reportBase}?${query}`}
            traceUrlBase={CDR_TRACE_REPORT}
            initialRange={campaignRange(campaign)}
            minDate={campaignDay(campaign.start_date)?.toDate()}
          />
        ) : null}
      </div>
    </FillPage>
  );
};

export default CampaignReport;
